// The RBP Shopping List reads (rbp `RbpController`, JwtAuthGuard):
//   GET /api/v1/rbp/eligibility                    { shoppingList } : the entry point's signal
//   GET /api/v1/rbp/shopping-list/header           the header, or 204 with no report yet
//   GET /api/v1/rbp/shopping-list/benefit-groups   the group rows, in benefit-group code order
//   GET /api/v1/rbp/shopping-list/benefit-groups/:benefitGroupCode/lines?search=
//                                                  the expanded group: sub-groups and their lines
// and the store's selection writes:
//   PUT    .../benefit-groups/:benefitGroupCode/selections/:productCode  { quantity } (0 removes)
//   DELETE .../benefit-groups/:benefitGroupCode/selections               clear the group (204)
//   DELETE /api/v1/rbp/shopping-list/selections                          clear everything (204)
// and the add to cart:
//   GET  /api/v1/rbp/shopping-list/cart-additions/preview?benefitGroupCode=  the confirmation
//   POST /api/v1/rbp/shopping-list/cart-additions  { benefitGroupCode? }     the add (201)
// The wire shapes are a LOCK-STEP COPY of the rbp contracts (`RbpEligibilityResponse`,
// `RbpShoppingListHeaderResponse`, `RbpBenefitGroupsResponse`, `RbpGroupLinesResponse`,
// `RbpSelectionResponseWire`, `RBP_SELECTION_MAX_QUANTITY`, `RbpCartAdditionPreviewWire` and
// `RbpCartAdditionResultWire` in
// `libs/modules/rbp/src/contracts/dtos/`); keep the two in sync. The API leaves a null field out,
// so an absent optional field reads as null here.
//
// Every read THROWS on a non-2xx with its status (`RbpRequestError`): a 404 is "this store has no
// list", which the page shows as such, and anything else is a failure with a retry. A malformed
// row is dropped and a malformed optional field read as null, as the ordering-choices read does;
// a malformed header or a body of the wrong kind throws.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const RBP_PATH = `${API_PREFIX}/rbp`;

export type RbpTrafficLight = 'GREEN' | 'ORANGE' | 'RED';

export interface RbpBenefitGroupRef {
  readonly code: string;
  readonly name: string;
}

export interface RbpShoppingListHeader {
  readonly quarter: { readonly year: number; readonly quarter: number };
  /** `YYYY-MM-DD`; null until the admin sets the quarter's close date. */
  readonly quarterClosesOn: string | null;
  /** `YYYY-MM-DD`: the report's date. */
  readonly reportedOn: string;
  readonly productsOrdered: number;
  readonly benefitGroupsQualified: number;
  readonly productsSelected: number;
  /** A UTC instant: when the store's selections last changed; null with none. */
  readonly lastEditedAt: string | null;
  /** The programme's non-payable groups, for the banner that names them. */
  readonly nonPayableBenefitGroups: readonly RbpBenefitGroupRef[];
}

/** The expanded group's progress bar: each segment and the min marker as 0 to 100 (RBP-D15). */
export interface RbpProgressBar {
  readonly orderedPct: number;
  readonly inCartPct: number;
  readonly selectedPct: number;
  readonly minMarkerPct: number;
}

export interface RbpBenefitGroupRow {
  readonly benefitGroupCode: string;
  readonly description: string;
  readonly isRebatePayable: boolean;
  readonly purchasedPct: number;
  readonly eligibleThresholdPct: number;
  readonly highlyEligibleThresholdPct: number;
  readonly trafficLight: RbpTrafficLight;
  readonly clusterCode: string | null;
  readonly minLinesRequired: number;
  readonly maxLinesRequired: number;
  readonly productCount: number;
  readonly productsOrdered: number;
  readonly productsInCarts: number;
  readonly productsSelected: number;
  /** Null when the server's bar is missing or malformed: the bar is then not drawn. */
  readonly progressBar: RbpProgressBar | null;
}

/** A line's state as SL1.0 shows it (RBP-D22). */
export type RbpLineState =
  | 'NOT_ON_SHOPLINK'
  | 'UNAVAILABLE'
  | 'PICKED_FRESH'
  | 'TEMPORARILY_OUT_OF_STOCK'
  | 'NO_STOCK_DETAILS'
  | 'IN_STOCK';

/** A linked product offered in place of an unavailable line (RBP-D23). */
export interface RbpLineAlternative {
  readonly productCode: string;
  readonly description: string | null;
  readonly packSize: string | null;
  readonly state: RbpLineState;
}

/** One unpurchased line of the expanded group. */
export interface RbpPositionLine {
  /** Null for a line with no BWG product code. */
  readonly productCode: string | null;
  readonly supplierName: string | null;
  readonly supplierProductCode: string | null;
  readonly description: string | null;
  readonly packSize: string | null;
  /** The RBP range the line is ordered in; null when not on ShopLink. */
  readonly assortmentCode: string | null;
  /** `YYYY-MM-DD`; null when never ordered. */
  readonly lastInvoicedOn: string | null;
  readonly hasOrdered: boolean;
  readonly state: RbpLineState;
  readonly isSelectable: boolean;
  readonly alternativeProductCode: string | null;
  readonly selectedQuantity: number;
  readonly isInCart: boolean;
  readonly alternative: RbpLineAlternative | null;
}

/** One sub-group of the expanded group, kept even with no lines. */
export interface RbpSubGroupLines {
  readonly benefitSubGroupCode: string;
  readonly name: string;
  readonly lines: readonly RbpPositionLine[];
}

/**
 * A refused or failed RBP request, with its HTTP status and, for a refusal the server types (a 409
 * of the add to cart), its problem type (`rbp-nothing-to-add`, `cart-sending`, `cart-contended`).
 */
export class RbpRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly problemType: string | null = null,
  ) {
    super(message);
    this.name = 'RbpRequestError';
  }
}

async function getJson(path: string, what: string): Promise<{ status: number; body: unknown }> {
  const res = await apiFetch(`${RBP_PATH}${path}`);
  if (!res.ok) throw new RbpRequestError(res.status, `Failed to load ${what} (${res.status})`);
  if (res.status === 204) return { status: 204, body: null };
  return { status: res.status, body: await res.json() };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const optionalString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

function malformed(what: string): never {
  throw new Error(`Malformed ${what} response`);
}

/** `GET /api/v1/rbp/eligibility`: whether to draw the "RBP Shopping List" entry point. */
export async function fetchRbpEligibility(): Promise<boolean> {
  const { body } = await getJson('/eligibility', 'the RBP eligibility');
  if (!isRecord(body) || typeof body.shoppingList !== 'boolean') malformed('RBP eligibility');
  return body.shoppingList;
}

/** `GET /api/v1/rbp/shopping-list/header`; null for a 204 (no current report or no position). */
export async function fetchRbpShoppingListHeader(): Promise<RbpShoppingListHeader | null> {
  const { status, body } = await getJson('/shopping-list/header', 'the RBP header');
  if (status === 204) return null;
  if (!isRecord(body)) malformed('RBP header');
  const quarter = body.quarter;
  if (
    !isRecord(quarter) ||
    !isCount(quarter.year) ||
    !isCount(quarter.quarter) ||
    typeof body.reportedOn !== 'string' ||
    !isCount(body.productsOrdered) ||
    !isCount(body.benefitGroupsQualified) ||
    !isCount(body.productsSelected)
  ) {
    malformed('RBP header');
  }
  const nonPayable = Array.isArray(body.nonPayableBenefitGroups)
    ? body.nonPayableBenefitGroups.flatMap((group) =>
        isRecord(group) && typeof group.code === 'string' && typeof group.name === 'string'
          ? [{ code: group.code, name: group.name }]
          : [],
      )
    : [];
  return {
    quarter: { year: quarter.year, quarter: quarter.quarter },
    quarterClosesOn: optionalString(body.quarterClosesOn),
    reportedOn: body.reportedOn,
    productsOrdered: body.productsOrdered,
    benefitGroupsQualified: body.benefitGroupsQualified,
    productsSelected: body.productsSelected,
    lastEditedAt: optionalString(body.lastEditedAt),
    nonPayableBenefitGroups: nonPayable,
  };
}

const TRAFFIC_LIGHTS: readonly string[] = ['GREEN', 'ORANGE', 'RED'];

function parseRow(row: unknown): RbpBenefitGroupRow | null {
  if (!isRecord(row)) return null;
  const {
    benefitGroupCode,
    description,
    isRebatePayable,
    purchasedPct,
    eligibleThresholdPct,
    highlyEligibleThresholdPct,
    trafficLight,
    minLinesRequired,
    maxLinesRequired,
    productCount,
    productsOrdered,
    productsInCarts,
    productsSelected,
  } = row;
  if (
    typeof benefitGroupCode !== 'string' ||
    typeof description !== 'string' ||
    typeof isRebatePayable !== 'boolean' ||
    !isNumber(purchasedPct) ||
    !isNumber(eligibleThresholdPct) ||
    !isNumber(highlyEligibleThresholdPct) ||
    typeof trafficLight !== 'string' ||
    !TRAFFIC_LIGHTS.includes(trafficLight) ||
    !isNumber(minLinesRequired) ||
    !isNumber(maxLinesRequired) ||
    !isCount(productCount) ||
    !isCount(productsOrdered) ||
    !isCount(productsInCarts) ||
    !isCount(productsSelected)
  ) {
    return null;
  }
  return {
    benefitGroupCode,
    description,
    isRebatePayable,
    purchasedPct,
    eligibleThresholdPct,
    highlyEligibleThresholdPct,
    trafficLight: trafficLight as RbpTrafficLight,
    clusterCode: optionalString(row.clusterCode),
    minLinesRequired,
    maxLinesRequired,
    productCount,
    productsOrdered,
    productsInCarts,
    productsSelected,
    progressBar: parseProgressBar(row.progressBar),
  };
}

function parseProgressBar(value: unknown): RbpProgressBar | null {
  if (!isRecord(value)) return null;
  const { orderedPct, inCartPct, selectedPct, minMarkerPct } = value;
  if (!isNumber(orderedPct) || !isNumber(inCartPct) || !isNumber(selectedPct)) return null;
  if (!isNumber(minMarkerPct)) return null;
  return { orderedPct, inCartPct, selectedPct, minMarkerPct };
}

/** `GET /api/v1/rbp/shopping-list/benefit-groups`: the rows, in the server's order. */
export async function fetchRbpBenefitGroups(): Promise<readonly RbpBenefitGroupRow[]> {
  const { body } = await getJson('/shopping-list/benefit-groups', 'the RBP benefit groups');
  if (!Array.isArray(body)) malformed('RBP benefit groups');
  return body.flatMap((row) => {
    const parsed = parseRow(row);
    return parsed === null ? [] : [parsed];
  });
}

const LINE_STATES: readonly string[] = [
  'NOT_ON_SHOPLINK',
  'UNAVAILABLE',
  'PICKED_FRESH',
  'TEMPORARILY_OUT_OF_STOCK',
  'NO_STOCK_DETAILS',
  'IN_STOCK',
];
const isLineState = (value: unknown): value is RbpLineState =>
  typeof value === 'string' && LINE_STATES.includes(value);

function parseAlternative(value: unknown): RbpLineAlternative | null {
  if (!isRecord(value) || typeof value.productCode !== 'string' || !isLineState(value.state)) {
    return null;
  }
  return {
    productCode: value.productCode,
    description: optionalString(value.description),
    packSize: optionalString(value.packSize),
    state: value.state,
  };
}

function parseLine(line: unknown): RbpPositionLine | null {
  if (!isRecord(line)) return null;
  const { state, isSelectable, hasOrdered, isInCart, selectedQuantity } = line;
  if (
    !isLineState(state) ||
    typeof isSelectable !== 'boolean' ||
    typeof hasOrdered !== 'boolean' ||
    typeof isInCart !== 'boolean' ||
    !isCount(selectedQuantity)
  ) {
    return null;
  }
  return {
    productCode: optionalString(line.productCode),
    supplierName: optionalString(line.supplierName),
    supplierProductCode: optionalString(line.supplierProductCode),
    description: optionalString(line.description),
    packSize: optionalString(line.packSize),
    assortmentCode: optionalString(line.assortmentCode),
    lastInvoicedOn: optionalString(line.lastInvoicedOn),
    hasOrdered,
    state,
    isSelectable,
    alternativeProductCode: optionalString(line.alternativeProductCode),
    selectedQuantity,
    isInCart,
    alternative: parseAlternative(line.alternative),
  };
}

/**
 * `GET /api/v1/rbp/shopping-list/benefit-groups/:benefitGroupCode/lines`: the expanded group's
 * sub-groups in the server's order. The group code is a path segment, encoded; the search text
 * travels as the `search` parameter through `URLSearchParams`, never concatenated, and is left out
 * when blank (every line is listed).
 */
export async function fetchRbpGroupLines(
  benefitGroupCode: string,
  search: string | null,
): Promise<readonly RbpSubGroupLines[]> {
  const params = new URLSearchParams();
  if (search !== null && search.trim() !== '') params.set('search', search);
  const encoded = params.toString();
  const query = encoded === '' ? '' : `?${encoded}`;
  const { body } = await getJson(
    `/shopping-list/benefit-groups/${encodeURIComponent(benefitGroupCode)}/lines${query}`,
    'the RBP group lines',
  );
  if (!Array.isArray(body)) malformed('RBP group lines');
  return body.flatMap((subGroup) => {
    if (
      !isRecord(subGroup) ||
      typeof subGroup.benefitSubGroupCode !== 'string' ||
      typeof subGroup.name !== 'string' ||
      !Array.isArray(subGroup.lines)
    ) {
      return [];
    }
    return [
      {
        benefitSubGroupCode: subGroup.benefitSubGroupCode,
        name: subGroup.name,
        lines: subGroup.lines.flatMap((line) => {
          const parsed = parseLine(line);
          return parsed === null ? [] : [parsed];
        }),
      },
    ];
  });
}

/** The largest quantity a selection may hold: a copy of rbp's `RBP_SELECTION_MAX_QUANTITY`. */
export const RBP_SELECTION_MAX_QUANTITY = 100_000;

/** A stored selection, as a write answers it. */
export interface RbpSelection {
  readonly productCode: string;
  /** 0 after a removal. */
  readonly selectedQuantity: number;
  readonly alternativeProductCode: string | null;
}

/**
 * A refused or failed write. For a refusal the server explains (a 400 for the quantity, a 409 for
 * a line that cannot take it), the message is the Problem Details `detail`, so the line can say why.
 */
async function writeFailure(res: Response, what: string): Promise<RbpRequestError> {
  let detail: string | null = null;
  if (res.status === 400 || res.status === 409) {
    try {
      const body: unknown = await res.json();
      if (isRecord(body) && typeof body.detail === 'string') detail = body.detail;
    } catch {
      // A refusal without a readable body keeps the generic message.
    }
  }
  return new RbpRequestError(res.status, detail ?? `Failed to ${what} (${res.status})`);
}

const selectionsPath = (benefitGroupCode: string) =>
  `${RBP_PATH}/shopping-list/benefit-groups/${encodeURIComponent(benefitGroupCode)}/selections`;

/**
 * `PUT .../benefit-groups/:benefitGroupCode/selections/:productCode`: store the absolute quantity
 * the screen shows for the line; 0 removes it. Both codes are encoded path segments.
 */
export async function setRbpSelection(
  benefitGroupCode: string,
  productCode: string,
  quantity: number,
): Promise<RbpSelection> {
  const res = await apiFetch(
    `${selectionsPath(benefitGroupCode)}/${encodeURIComponent(productCode)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity }),
    },
  );
  if (!res.ok) throw await writeFailure(res, 'save the selection');
  const body: unknown = await res.json();
  if (!isRecord(body) || typeof body.productCode !== 'string' || !isCount(body.selectedQuantity)) {
    malformed('RBP selection');
  }
  return {
    productCode: body.productCode,
    selectedQuantity: body.selectedQuantity,
    alternativeProductCode: optionalString(body.alternativeProductCode),
  };
}

/** `DELETE .../benefit-groups/:benefitGroupCode/selections`: clear the store's group (204). */
export async function deselectRbpGroup(benefitGroupCode: string): Promise<void> {
  const res = await apiFetch(selectionsPath(benefitGroupCode), { method: 'DELETE' });
  if (!res.ok) throw await writeFailure(res, 'deselect the group');
}

/** `DELETE /api/v1/rbp/shopping-list/selections`: clear every selection of the store (204). */
export async function deselectRbpAll(): Promise<void> {
  const res = await apiFetch(`${RBP_PATH}/shopping-list/selections`, { method: 'DELETE' });
  if (!res.ok) throw await writeFailure(res, 'deselect everything');
}

/** One depot row of the add-to-cart confirmation: the range, its depot, SL1.0's name, the lines. */
export interface RbpCartAdditionDepot {
  readonly assortmentCode: string;
  readonly depotCode: string;
  readonly displayName: string;
  readonly lineCount: number;
}

/** The confirmation; `depots` empty means "No products to add!". */
export interface RbpCartAdditionPreview {
  readonly depots: readonly RbpCartAdditionDepot[];
  readonly lineCount: number;
}

/** What an add did: the depots written, the counts and when (a UTC instant). */
export interface RbpCartAdditionResult {
  readonly depots: ReadonlyArray<RbpCartAdditionDepot & { readonly addedCount: number }>;
  readonly addedCount: number;
  readonly notAddedCount: number;
  readonly addedAt: string;
  /** False when the carts took the lines but the selections stayed selected (adding them again
   *  would add their quantities twice). An older server sends no flag: read as true. */
  readonly selectionsCleared: boolean;
}

/** The problem type of a typed refusal: the header the api echoes, else the `type` URI's slug. */
function problemTypeOf(res: Response, body: unknown): string | null {
  const header = res.headers.get('x-shoplink-problem-type');
  if (header !== null && header !== '') return header;
  if (isRecord(body) && typeof body.type === 'string') {
    const slug = body.type.split('/').pop();
    return slug === undefined || slug === '' ? null : slug;
  }
  return null;
}

/** A refused add: its status, the Problem Details `detail` and the problem type when typed. */
async function cartAdditionFailure(res: Response, what: string): Promise<RbpRequestError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // A refusal without a readable body keeps the generic message.
  }
  const detail = isRecord(body) && typeof body.detail === 'string' ? body.detail : null;
  return new RbpRequestError(
    res.status,
    detail ?? `Failed to ${what} (${res.status})`,
    problemTypeOf(res, body),
  );
}

function parseDepot(value: unknown): RbpCartAdditionDepot | null {
  if (
    !isRecord(value) ||
    typeof value.assortmentCode !== 'string' ||
    typeof value.displayName !== 'string' ||
    !isCount(value.lineCount)
  ) {
    return null;
  }
  return {
    assortmentCode: value.assortmentCode,
    depotCode: optionalString(value.depotCode) ?? '',
    displayName: value.displayName,
    lineCount: value.lineCount,
  };
}

/**
 * `GET .../cart-additions/preview`: per depot, how many selected lines an add would put in a cart.
 * The group code travels as the `benefitGroupCode` parameter through `URLSearchParams`; null asks
 * about every selection.
 */
export async function fetchRbpCartAdditionPreview(
  benefitGroupCode: string | null,
): Promise<RbpCartAdditionPreview> {
  const params = new URLSearchParams();
  if (benefitGroupCode !== null) params.set('benefitGroupCode', benefitGroupCode);
  const encoded = params.toString();
  const res = await apiFetch(
    `${RBP_PATH}/shopping-list/cart-additions/preview${encoded === '' ? '' : `?${encoded}`}`,
  );
  if (!res.ok) throw await cartAdditionFailure(res, 'prepare the add to cart');
  const body: unknown = await res.json();
  if (!isRecord(body) || !Array.isArray(body.depots) || !isCount(body.lineCount)) {
    malformed('RBP cart addition preview');
  }
  return {
    depots: body.depots.flatMap((depot) => {
      const parsed = parseDepot(depot);
      return parsed === null ? [] : [parsed];
    }),
    lineCount: body.lineCount,
  };
}

/**
 * `POST .../cart-additions`: add the store's selections (one group's, or every one with null) to
 * its depot carts. A 409 carries its problem type, so the screen can tell "nothing to add" from a
 * cart being sent.
 */
export async function addRbpSelectionsToCart(
  benefitGroupCode: string | null,
): Promise<RbpCartAdditionResult> {
  const res = await apiFetch(`${RBP_PATH}/shopping-list/cart-additions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(benefitGroupCode === null ? {} : { benefitGroupCode }),
  });
  if (!res.ok) throw await cartAdditionFailure(res, 'add to cart');
  const body: unknown = await res.json();
  if (
    !isRecord(body) ||
    !Array.isArray(body.depots) ||
    !isCount(body.addedCount) ||
    !isCount(body.notAddedCount) ||
    typeof body.addedAt !== 'string'
  ) {
    malformed('RBP cart addition');
  }
  return {
    depots: body.depots.flatMap((depot) => {
      const parsed = parseDepot(depot);
      if (parsed === null || !isRecord(depot) || !isCount(depot.addedCount)) return [];
      return [{ ...parsed, addedCount: depot.addedCount }];
    }),
    addedCount: body.addedCount,
    notAddedCount: body.notAddedCount,
    addedAt: body.addedAt,
    selectionsCleared: body.selectionsCleared !== false,
  };
}

// The cart's RBP core marks: `GET /api/v1/rbp/core-marks?catalogue=&skus=`. The wire shape is a
// LOCK-STEP COPY of rbp's `RbpCoreMarksResponse` (libs/modules/rbp contracts/dtos/
// rbp-core-range.dto.ts). A product that is not core for the caller's store is absent.
//
// The marks are advisory and never an error the cart has to show. The SKUs go in chunks of the
// server's cap, and a code outside the server's pattern is never sent (it has no mark). A refused
// caller (403) or a route not deployed (404) answers "no marks"; a chunk that fails otherwise, or
// returns a drifted body, reports its SKUs as unread, so a cache can ask again later.

export type RbpCoreMark = 'CORE' | 'LINK';

export const CORE_MARKS_PATH = `${API_PREFIX}/rbp/core-marks`;

/** The server's cap on `skus` (rbp's `core-marks.validation.ts`, lock-step). */
export const MAX_CORE_MARKS_SKUS = 250;

/** The codes the server reads (rbp's `core-marks.validation.ts`, lock-step); any other has no mark. */
const SKU_PATTERN = /^[A-Za-z0-9-]{1,32}$/;

export interface CoreMarksReadResult {
  /** The asked SKUs that are core for the caller's store; an absent read SKU is not core. */
  readonly marks: Record<string, RbpCoreMark>;
  /** SKUs whose chunk failed or came back drifted: unknown, not "not core". */
  readonly unreadSkus: readonly string[];
}

/**
 * The catalogues that show no RBP feature: WOW, Elite, LSN and Corrib Retail (SL1.0
 * `www/engine/get_smanager.php:1174`). A mirror of rbp's `RBP_BROWSE_SIDE_EXCLUDED_CATALOGUES`,
 * which the web cannot import (the barrel carries the Nest module); the names are the web's
 * ordering-entry kinds. Value Centres are in scope.
 */
export const RBP_EXCLUDED_ENTRY_KINDS: readonly string[] = ['WOW', 'ELITE', 'LSN', 'CORRIB_RETAIL'];

export function isRbpEntryKind(kind: string): boolean {
  return !RBP_EXCLUDED_ENTRY_KINDS.includes(kind);
}

function isMarks(value: unknown): value is Record<string, RbpCoreMark> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((mark) => mark === 'CORE' || mark === 'LINK')
  );
}

/** The core mark of each asked SKU that is core for the caller's store. Never rejects. */
export async function fetchCoreMarks(
  catalogueKind: string,
  skus: readonly string[],
): Promise<CoreMarksReadResult> {
  const unique = [...new Set(skus)].filter((sku) => SKU_PATTERN.test(sku));
  const chunks: string[][] = [];
  for (let start = 0; start < unique.length; start += MAX_CORE_MARKS_SKUS) {
    chunks.push(unique.slice(start, start + MAX_CORE_MARKS_SKUS));
  }
  const results = await Promise.all(chunks.map((chunk) => readChunk(catalogueKind, chunk)));
  return {
    marks: Object.assign({}, ...results.map((result) => result.marks)) as Record<
      string,
      RbpCoreMark
    >,
    unreadSkus: results.flatMap((result) => result.unreadSkus),
  };
}

async function readChunk(catalogueKind: string, chunk: string[]): Promise<CoreMarksReadResult> {
  try {
    const params = new URLSearchParams({ catalogue: catalogueKind, skus: chunk.join(',') });
    const res = await apiFetch(`${CORE_MARKS_PATH}?${params.toString()}`);
    if (res.status === 403 || res.status === 404) return { marks: {}, unreadSkus: [] };
    if (!res.ok) return { marks: {}, unreadSkus: chunk };
    const body: unknown = await res.json();
    const marks =
      typeof body === 'object' && body !== null ? (body as { marks?: unknown }).marks : undefined;
    return isMarks(marks)
      ? { marks: { ...marks }, unreadSkus: [] }
      : { marks: {}, unreadSkus: chunk };
  } catch {
    return { marks: {}, unreadSkus: chunk };
  }
}
